<template>
  <div>
    <DialogLayout v-show="step === 'apply'" :class-name="'job-application-dialog'" @close="handleClose">
      <template #title>{{ t('candidate.applyJob') }}</template>
      <template #default>
        <el-form ref="formRef" :model="formData" :rules="rules" label-position="top" class="application-form">
          <el-form-item v-if="!isBiz" :label="t('employee.filters.enterpriseName')" prop="enterpriseId">
            <el-select v-model="formData.enterpriseId" :placeholder="t('common.pleaseSelect')" filterable
              @change="handleEnterpriseChange">
              <el-option v-for="enterprise in enterpriseOptions" :key="enterprise.id" :label="enterprise.name"
                :value="enterprise.id">
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item :label="t('candidate.job')" prop="jobId">
            <el-select v-model="formData.jobId" :placeholder="t('common.pleaseSelect')" filterable :loading="jobsLoading"
              fit-input-width popper-class="job-application-select-dropdown">
              <el-option v-for="job in sortedJobOptions" :key="job.jobId" :label="`${job.jobId} | ${job.title}`"
                :value="job.jobId">
                <JobInfo :job="job" :tag="matchTag(job.matchStatus)" :reserve-tag="isLincAgent" variant="option" />
              </el-option>
            </el-select>
          </el-form-item>

          <el-form-item prop="reason" class="reason-form-item">
            <template #label>
              {{ t('candidate.recommendationReason') }}
              <!-- 绝对定位到 label 右侧，不影响 label 后置的必填星号 -->
              <el-button v-if="isLincAgent" link type="primary" class="ai-generate-button" :disabled="generating"
                @click.prevent="handleAiGenerate">
                <Icon name="ai-extract" style="margin-right: 4px;" />{{ t('jobMatch.applyDialog.aiGenerate') }}
              </el-button>
            </template>
            <el-input v-model="formData.reason" v-loading="generating" type="textarea"
              :placeholder="t('common.pleaseEnter')" :rows="4" :maxlength="3000" show-word-limit />
          </el-form-item>
        </el-form>
      </template>
      <template #footer>
        <el-checkbox v-if="!isBiz" v-model="authorized" class="authorized-checkbox">
          {{ t('jobMatch.applyDialog.authorized') }}
        </el-checkbox>
        <el-button class="confirm-button-default" @click="handleCancel">
          {{ t('candidate.cancel') }}
        </el-button>
        <el-button type="primary" :loading="submitting" :disabled="generating" @click="handleConfirm">
          {{ t('candidate.apply') }}
        </el-button>
      </template>
    </DialogLayout>
    <!-- AI 生成推荐理由：同一个弹窗内切换，投递表单用 v-show 隐藏以保留已填内容 -->
    <AiRecommendReasonDialog v-if="step === 'ai'" :candidate-id="props.candidateId!" :job-id="formData.jobId!"
      @confirm="handleAiConfirm" @cancel="step = 'apply'" @close="step = 'apply'" />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import { ElMessage, ElMessageBox } from 'element-plus'
import DialogLayout from '@/components/business-components/dialog/dialog-layout.vue'
import AiRecommendReasonDialog from './ai-recommend-reason-dialog.vue'
import JobInfo from '@/components/business-components/job-info/index.vue'
import { Icon } from '@/components/basic-components/icon'
import { getCandidateJobsApi, getHeadhuntingJobCandidateJobsApi, getHeadhuntingEnterpriseListApi, applyJobApi } from '@/api/candidate'
import { checkDuplicateApplicationApi } from '@/api/interview'
import {
  JobMatchStatus,
  applyMatchJobApi,
  checkMatchDuplicateApplicationApi,
  generateRecommendReasonApi,
  getMatchApplyEnterpriseOptionsApi,
} from '@/api/job-match'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/store/modules/user'
import { System } from '@/router/config/roles'
import { useHunterOnlySections } from '@/composables/useHunterOnlySections'
import { useJobMatchTag } from '@/composables/useJobMatchTag'
import type { JobApplicationDialogProps } from './job-application-dialog-types'

const { t } = useI18n()
const userStore = useUserStore()

interface JobOption {
  jobId: number
  title: string
  jobType?: number
  affiliationName: string
  positionTypeName?: string
  employmentFormStr?: string
  createTime?: string
  /** 岗位匹配状态（仅 Linc Agent 下有，用来展示标签和排序） */
  matchStatus?: JobMatchStatus
}

interface EnterpriseOption {
  id: number
  name: string
}

const isBiz = computed(() => userStore.userSystem == System.business)
// 岗位匹配相关（标签、排序、AI 生成）仅 Linc Agent 可用
const { showHunterOnlySections: isLincAgent } = useHunterOnlySections()

const props = defineProps<JobApplicationDialogProps>()

const emit = defineEmits<{
  cancel: []
  confirm: []
  close: []
}>()

const enterpriseOptions = ref<EnterpriseOption[]>([])
const formRef = ref<FormInstance>()
const submitting = ref(false)
const jobsLoading = ref(false)
const jobOptions = ref<JobOption[]>([])
const step = ref<'apply' | 'ai'>('apply')
const generating = ref(false)
// 用户已授权投递，默认勾选
const authorized = ref(true)

const formData = reactive({
  jobId: props.presetJobId as number | undefined,
  enterpriseId: props.presetEnterpriseId as number | undefined,
  reason: ''
})

const rules = computed<FormRules>(() => ({
  enterpriseId: [
    { required: true, message: t('candidate.pleaseSelectEnterprise'), trigger: 'blur' }
  ],
  jobId: [
    { required: true, message: t('candidate.pleaseSelectJob'), trigger: 'blur' }
  ],
  reason: [
    { required: true, message: t('candidate.pleaseEnterReason'), trigger: 'blur' }
  ]
}))

// Linc Agent 下展示岗位匹配状态标签并按状态排序
const { matchTag: getMatchTag, sortByMatchStatus } = useJobMatchTag()
const matchTag = (status?: JobMatchStatus) => (isLincAgent.value ? getMatchTag(status) : undefined)
const sortedJobOptions = computed(() => (isLincAgent.value ? sortByMatchStatus(jobOptions.value) : jobOptions.value))

// 加载职位列表
const loadJobs = async () => {
  jobsLoading.value = true
  try {
    let response
    if (isBiz.value) {
      response = await getCandidateJobsApi(props.candidateId!)
    } else {
      // 猎头端：职位接口已带 matchStatus，Linc Agent 下据此展示标签和排序
      response = await getHeadhuntingJobCandidateJobsApi(props.candidateId!, formData.enterpriseId!)
    }
    jobOptions.value = response || []
  } catch (error) {
    console.error('加载职位列表失败:', error)
    jobOptions.value = []
  } finally {
    jobsLoading.value = false
  }
}

const handleEnterpriseChange = () => {
  formData.jobId = undefined
  loadJobs()
}

// 组件挂载时加载职位列表
onMounted(() => {
  if (!isBiz.value) {
    const enterpriseApi = isLincAgent.value ? getMatchApplyEnterpriseOptionsApi : getHeadhuntingEnterpriseListApi
    enterpriseApi().then(res => {
      enterpriseOptions.value = res || []
    })
    // 从岗位匹配投递时会带上企业，直接加载该企业下的职位
    if (formData.enterpriseId) {
      loadJobs()
    }
  } else {
    loadJobs()
  }
})

const handleCancel = () => {
  emit('cancel')
}

// AI 自动生成：先校验企业和职位，再切到选择面谈纪要
const handleAiGenerate = async () => {
  try {
    await formRef.value?.validateField(['enterpriseId', 'jobId'])
  } catch {
    return
  }
  step.value = 'ai'
}

const handleAiConfirm = async (bookingId: number) => {
  step.value = 'apply'
  generating.value = true
  try {
    const { reason } = await generateRecommendReasonApi({
      candidateId: props.candidateId!,
      jobId: formData.jobId!,
      bookingId,
    })
    formData.reason = reason || ''
    formRef.value?.clearValidate('reason')
  } catch (error) {
    ElMessage.error((error as { message?: string })?.message || t('common.operationFailed'))
  } finally {
    generating.value = false
  }
}

const handleConfirm = async () => {
  if (!formRef.value) return

  try {
    await formRef.value.validate()
  } catch {
    return
  }

  if (!isBiz.value && !authorized.value) {
    ElMessage.warning(t('jobMatch.applyDialog.pleaseConfirmAuthorized'))
    return
  }

  const checkDuplicate = isLincAgent.value ? checkMatchDuplicateApplicationApi : checkDuplicateApplicationApi
  const apply = isLincAgent.value ? applyMatchJobApi : applyJobApi

  submitting.value = true
  try {
    // 投递前重复校验
    const result = await checkDuplicate({
      candidateId: props.candidateId!,
      jobId: formData.jobId!
    })

    if (result.isDuplicate === true) {
      try {
        await ElMessageBox.confirm(result.duplicateMessage, t('candidate.applyConfirmTitle'), {
          confirmButtonText: t('candidate.apply'),
          cancelButtonText: t('common.cancel'),
          type: 'warning'
        })
      } catch {
        // 用户取消重复投递确认，保持弹窗不关闭
        return
      }
    }

    await apply({
      candidateId: props.candidateId!,
      jobId: formData.jobId!,
      reason: formData.reason
    })

    ElMessage.success(t('candidate.applySuccess'))
    emit('confirm')
  } catch (error) {
    const errorMessage =
      (error as { message?: string })?.message ||
      (typeof error === 'string' ? error : '')
    if (errorMessage) {
      ElMessage.error(errorMessage || t('common.operationFailed'))
    }
  } finally {
    submitting.value = false
  }
}

const handleClose = () => {
  emit('close')
}
</script>

<style lang="scss" scoped>
.ai-generate-button {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
}

// 叠加 .el-checkbox 提高优先级：Element Plus 的 .el-checkbox:last-of-type { margin-right: 0 } 与单类 scoped 同级，会覆盖 auto
.authorized-checkbox.el-checkbox {
  flex-shrink: 0;
  margin-right: auto;
  padding-right: 16px;
}

.application-form {
  :deep(.el-form-item) {
    margin-bottom: 20px;

    .el-form-item__label {
      font-size: 14px;
      font-weight: 500;
      color: #212429;
      line-height: 20px;
      margin-bottom: 8px;
    }

    .el-form-item__content {
      line-height: normal;
    }
  }

  :deep(.el-select) {
    width: 100%;
  }

  // 给 AI 自动生成按钮做定位参照
  :deep(.reason-form-item .el-form-item__label) {
    position: relative;
    width: 100%;
  }

  :deep(.el-textarea__inner) {
    font-family: inherit;
  }
}


</style>

<!-- 非 scoped 样式，用于下拉菜单（挂载在 body 上） -->
<style lang="scss">
// 限制最大高度：标题和底部按钮固定，内容区（含可拖高的文本框）滚动
.job-application-dialog.dialog-wrap {
  display: flex;
  flex-direction: column;
  min-width: 560px;
  max-height: 60vh;
  background: #ffffff;

  .dialog-header,
  .dialog-footer {
    flex-shrink: 0;
  }

  // flex 子项默认 min-height: auto 不会比内容矮，必须置 0 才能在超出时滚动
  .dialog-content {
    min-height: 0;
    overflow-y: auto;
  }
}

// 选中项中的 span 样式 - 只作用于当前组件的下拉菜单
.job-application-select-dropdown {
  .el-select-dropdown__item {
      height: 55px !important;
      // 右侧留出全局选中对勾（select.scss，right: 20px、宽 16px）的位置，避免和状态标签重叠；
      // 所有选项统一留白，标签在选中/未选中时位置不变
      padding: 6px 12px 6px 12px !important;
      line-height: normal !important;
      // margin: 0 10px;
      border-radius: 8px;
  
      &:hover {
        background-color: #F4EFFD !important;
      }
  
      &.selected {
        background-color: #F4EFFD !important;
      }
  
      &.active {
        background-color: #F4EFFD !important;
      }
  
      &.focus {
        background-color: #F4EFFD !important;
      }
    }
  
    .el-select-dropdown__item.is-hovering {
      background-color: #F4EFFD !important;
    }
    .el-select-dropdown__list {
        .el-select-dropdown__item {
            &.is-selected {
                font-weight: 500 !important;
            }
            &::after {
              right: 0;
              display: none;
            }
        }
    }
}
// .el-select-dropdown__item.is-selected::after {

// }
</style>

